import { Fragment, type FC } from 'react'
import ZoomAppRemovePage from '../../../../../assets/images/zoom-integration-remove-1024w.webp'
import ZoomCompanionModeOn from '../../../../../assets/images/zoom-companion-mode-on.png'
import ZoomCompanionModeOff from '../../../../../assets/images/zoom-companion-mode-off.png'
import ZoomAutomaticCallRecording from '../../../../../assets/images/zoom-automatic-call-recording.png'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { ZOOM_MEETINGS_HELP_ARTICLE_URL, ZOOM_PHONE_HELP_ARTICLE_URL } from '../../../../../common/integrations'

export const ZoomHowItWorks: FC = () => {
  return (
    <Fragment>
      <h5 className='mb-3'>Zoom Meetings</h5>
      <div>
        <p>
          Zocks' Zoom integration lets you have your Zocks AI Assistant with you on your Zoom meetings so you can focus on your client while Zocks captures
          accurate, actionable notes without storing a recording.
        </p>
        <p>
          The native Zoom plugin lets you access meeting prep materials, real-time client insights, and task management live during your meeting, without ever
          leaving Zoom.
        </p>
        <p>
          Need help? See the{' '}
          <a href={ZOOM_MEETINGS_HELP_ARTICLE_URL} target='_blank' rel='noopener noreferrer'>
            Zoom Meetings setup guide
          </a>
          .
        </p>
      </div>
      <ScopedBlock missingScopeAction={MissingScopeAction.HIDE} quota={{ quota: 'integration.zoomPhoneEnabled.value' }}>
        <h5 className='mb-3'>Zoom Phone calls</h5>
        <div>
          <p>
            Zocks can also turn your Zoom Phone calls into meeting notes. Turn on call recording in your Zoom account, then turn on capture in Your Settings.
            Zocks processes Zoom Phone side recordings to generate the transcript automatically.
          </p>
          <p>
            Need help? See the{' '}
            <a href={ZOOM_PHONE_HELP_ARTICLE_URL} target='_blank' rel='noopener noreferrer'>
              Zoom Phone calls setup guide
            </a>
            .
          </p>
        </div>
      </ScopedBlock>
      <h4 className='mt-5 mb-3'>Before you connect</h4>
      <div>
        <p>
          Zocks connects to whichever Zoom account you're currently signed into in your browser. If you have multiple Zoom accounts, you'll be asked to choose
          one when connecting.
        </p>
        <p>To connect the right account, sign out of Zoom in your browser first. Sign back in with the account you want to use, then return here to connect.</p>
      </div>
      <h4 className='mt-5 mb-3'>Wrong Zoom account connected?</h4>
      <div>
        <p>
          If you connected the wrong Zoom account, you can remove the Zocks app from Zoom's App Marketplace. Go to your installed apps, remove Zocks Meeting,
          then sign back in to Zoom with the correct account and reconnect here.
        </p>
        <p>
          Manage your installed Zoom apps at{' '}
          <a href='https://marketplace.zoom.us/user/installed' target='_blank' rel='noopener noreferrer'>
            marketplace.zoom.us/user/installed
          </a>
          .
        </p>
        <img src={ZoomAppRemovePage} alt='Zoom App Remove Page' className='mw-100' />
      </div>
      <ScopedBlock missingScopeAction={MissingScopeAction.HIDE} quota={{ quota: 'integration.zoomStrictJoinEnabled.value' }}>
        <h4 className='mt-5 mb-3'>About companion mode</h4>
        <div>
          <p>
            In companion mode, the AI Assistant joins as a named guest linked to your identity. It enters when you join and leaves when you do. Without
            companion mode, the AI Assistant joins as a separate participant when the meeting starts and stays in the meeting independently of you until the
            meeting ends.
          </p>
          <div className='row g-3 mt-2'>
            <div className='col-12 col-md-6'>
              <div className='d-flex align-items-center justify-content-center'>
                <img src={ZoomCompanionModeOn} alt='Zoom participants list in companion mode' className='mw-100' />
              </div>
              <p className='paragraph-small zocks-text-gray-600 text-center mt-2 mb-0'>In companion mode</p>
            </div>
            <div className='col-12 col-md-6'>
              <div className='d-flex align-items-center justify-content-center'>
                <img src={ZoomCompanionModeOff} alt='Zoom participants list with companion mode disabled' className='mw-100' />
              </div>
              <p className='paragraph-small zocks-text-gray-600 text-center mt-2 mb-0'>Disabled companion mode</p>
            </div>
          </div>
        </div>
      </ScopedBlock>
      <ScopedBlock missingScopeAction={MissingScopeAction.HIDE} quota={{ quota: 'integration.zoomPhoneEnabled.value' }}>
        <h4 className='mt-5 mb-3'>Turning on call recording in Zoom</h4>
        <div>
          <p>
            Automatic Call Recording is off by default in Zoom, so it needs to be turned on before Zocks can capture your Zoom Phone calls. In Zoom, go to{' '}
            <strong>Account Management</strong> → <strong>Account Settings</strong> → <strong>Zoom Phone</strong> → <strong>General</strong>, then turn on{' '}
            <strong>Automatic Call Recording</strong>. Only a Zoom admin can turn this on. If you're on a company Zoom account, ask your Zoom admin. If you own
            a personal paid Zoom account, you can turn it on yourself.
          </p>
          <img src={ZoomAutomaticCallRecording} alt='Zoom Automatic Call Recording setting' className='mw-100' />
        </div>
      </ScopedBlock>
    </Fragment>
  )
}
